import type { ColumnDefinition } from 'tabulator-tables';
import { MassiaTabulatorFilterPopupHelpers } from '../helpers/filter.popup.helpers';
import { MassiaTabulatorHelpers } from '../helpers/massia.tabulator.helpers';
import { applyScope } from '../helpers/scope.helpers';
import { MassiaTabulatorColumnDef, MassiaTabulatorFeatures, MassiaTabulatorGridConfig } from '../massia.tabulator.type';
import {
	ActionRegistry,
	ActionServerColumnDef,
	CheckboxServerColumnDef,
	CustomServerColumnDef,
	DateServerColumnDef,
	GridConfigDto,
	HrefDefinition,
	IconServerColumnDef,
	NumberServerColumnDef,
	ServerActionDef,
	ServerColumnDef,
	ServerFeaturesDef,
	TextServerColumnDef,
	TitleRegistry
} from './grid-config-adapter.types';
import { StateService } from '@uirouter/angularjs';

export default class GridConfigAdapterService {
	constructor(
		private $translate: ng.translate.ITranslateService,
		private globalizationManagementService: unknown,
		private moment: unknown
	) {}

	/**
	 * Adapte la configuration serveur en configuration Tabulator Massia.
	 *
	 * @param serverConfig - GridConfigDto retourné par le serveur
	 * @param actionRegistry - map { actionKey: handlerFn(rowData) }
	 * @param titleRegistry - map { titleKey: fn(rowData) => string }
	 */
	public adapt(
		serverConfig: GridConfigDto,
		actionRegistry: ActionRegistry,
		titleRegistry?: TitleRegistry,
		$scope?: ng.IScope,
		$compile?: ng.ICompileService,
		$state?: StateService
	): Omit<MassiaTabulatorGridConfig, 'options'> {
		const permissions = serverConfig.permissions?.rights || {};

		const columns: MassiaTabulatorColumnDef[] = (serverConfig.columns || []).map((col) =>
			this.buildColumn(col, actionRegistry, titleRegistry || {}, permissions, $scope, $compile, $state)
		);

		return {
			features: this.buildFeatures(serverConfig.features),
			events: {
				onRowClick: (e: UIEvent, rowData: unknown) => {
					const target = e.target as HTMLElement;
					if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.closest('button')) return;
					const handler = actionRegistry['rowClick'] || actionRegistry['visualize'];
					if (handler) handler(rowData, target.getAttribute('tabulator-field'));
				}
			},
			columns
		};
	}

	// ─── Features ────────────────────────────────────────────────────────

	private buildFeatures(f?: ServerFeaturesDef): MassiaTabulatorFeatures {
		if (!f) return {};
		const features: MassiaTabulatorFeatures = {
			search: f.search !== false,
			export: f.export !== false,
			globalMenu: f.globalMenu !== false,
			movableColumns: f.movableColumns !== false,
			localMode: f.localMode === true,
			rowDetails: f.rowDetails === true,
			tooltips: f.tooltips !== false,
			href: f.href,
			height: f.height,
			groupBy: f.groupBy,
			remote: f.remote as MassiaTabulatorFeatures['remote'],
			persistence: f.persistence
		};
		if (f.rowColorField) {
			// colorisaiton de ligne (conditionnel, ex: pour les facture en retard, ligne en rouge selon la propriété de l'objet isError)
			const colorField = f.rowColorField;
			const colorValue = f.rowColorValue || 'tomato';
			features.rowColor = function (rowData: Record<string, unknown>) {
				return rowData[colorField] ? colorValue : null;
			};
		}
		return features;
	}

	// ─── Dispatch par type ────────────────────────────────────────────────

	private buildColumn(
		col: ServerColumnDef,
		actionRegistry: ActionRegistry,
		titleRegistry: TitleRegistry,
		permissions: Record<string, boolean>,
		$scope?: ng.IScope,
		$compile?: ng.ICompileService,
		$state?: StateService
	): MassiaTabulatorColumnDef {
		switch (col.columnType) {
			case 'checkbox':
				return this.buildCheckbox(col, actionRegistry, $scope);
			case 'icon':
				return this.buildIcon(col, titleRegistry);
			case 'action':
				return this.buildActions(col, actionRegistry, permissions, $scope);
			case 'number':
				return this.buildNumber(col, $state);
			case 'date':
				return this.buildDate(col, $scope, $compile);
			case 'custom':
				return this.buildCustom(col, actionRegistry);
			case 'text':
				return this.buildText(col, $state);
			default: {
				const _exhaustive: never = col as never;
				if (_exhaustive) {
					console.error('Unknown column type:', _exhaustive);
				}
				return this.buildText(col as unknown as TextServerColumnDef);
			}
		}
	}

	// ─── Builders ────────────────────────────────────────────────────────
	// les builder sont appeler 1 seule fois à l'initialisation de la grille
	// mais la fonction formatter (lorsque celle-ci est définie dans le builder) est appelée pour chaque cellule, afin de decider de l'affichage. Sauf pour les type natif (géré par tabulator) comme text.

	private buildText(col: TextServerColumnDef, $state?: StateService): MassiaTabulatorColumnDef {
		const { headerFilter, headerFilterParams } = this.resolveHeaderFilter(col);
		let obj = {
			...this.buildColumnBase(col),
			hozAlign: col.hozAlign || 'left',
			headerFilter,
			headerFilterParams
		} as MassiaTabulatorColumnDef;
		if (col.href) {
			obj = { ...obj, formatter: this.buildHrefFormatter(col.href, $state) } as MassiaTabulatorColumnDef;
		}
		return obj;
	}

	private buildNumber(col: NumberServerColumnDef, $state?: StateService): MassiaTabulatorColumnDef {
		const symbolField = col.symbolField || 'deviseSymbole';
		let obj = {
			...this.buildColumnBase(col),
			hozAlign: 'right',
			formatter: function (cell: import('tabulator-tables').CellComponent) {
				const d = cell.getData() as Record<string, unknown>;
				const val = d[col.field] as number | string | null;
				return (val != null ? parseFloat(val.toString()).toFixed(2) : '') + (d[symbolField] ? ' ' + d[symbolField] : '');
			},
			headerFilter: col.filterable ? MassiaTabulatorFilterPopupHelpers.makeNumberRangeFilter : undefined
		} as MassiaTabulatorColumnDef;
		if (col.href) {
			obj = { ...obj, formatter: this.buildHrefFormatter(col.href, $state) } as MassiaTabulatorColumnDef;
		}
		return obj;
	}

	private buildDate(col: DateServerColumnDef, $scope?: ng.IScope, $compile?: ng.ICompileService): MassiaTabulatorColumnDef {
		return {
			...this.buildColumnBase(col),
			hozAlign: 'center',
			headerFilter: col.filterable ? (MassiaTabulatorFilterPopupHelpers.makeDateRangeFilter as ColumnDefinition['headerFilter']) : undefined,
			headerFilterParams: (col.filterable && $compile && $scope
				? { compile: $compile, parentScope: $scope, globalizationManagementService: this.globalizationManagementService, moment: this.moment }
				: undefined) as ColumnDefinition['headerFilterParams']
		} as MassiaTabulatorColumnDef;
	}

	private buildCheckbox(col: CheckboxServerColumnDef, actionRegistry: ActionRegistry, $scope?: ng.IScope): MassiaTabulatorColumnDef {
		const headerLabel = col.translateKey ? this.$translate.instant(col.translateKey) : '';
		let headerTooltip = headerLabel; //col.translateKey ? this.$translate.instant(col.translateKey + '_BULLE') : '';
		if (col.translateKey && this.$translate.instant(col.translateKey + '_BULLE') != col.translateKey + '_BULLE') {
			headerTooltip = this.$translate.instant(col.translateKey + '_BULLE');
		}
		const enabledWhenField = col.enabledWhenField;
		const w = col.width || 40;
		const field = col.field;
		const onChangeActionKey = col.onChangeActionKey;

		const colDef: MassiaTabulatorColumnDef = {
			title: this.resolveTitle(col) || col.field, // Titre texte pour le menu global
			_selectAllState: { active: false, field: field, enabledWhenField: enabledWhenField },
			titleFormatter: (cell: import('tabulator-tables').CellComponent) => {
				const container = this.buildTitleContainer(headerLabel, headerTooltip);

				const chk = document.createElement('input');
				chk.type = 'checkbox';
				chk.title = headerTooltip || headerLabel;
				// Synchronise UI avec l'état partagé
				if (colDef._selectAllState) {
					chk.checked = colDef._selectAllState.active;
				}

				chk.addEventListener('click', function (e) {
					e.stopPropagation();
					const checked = chk.checked;
					if (colDef._selectAllState) {
						colDef._selectAllState.active = checked;
					}

					// 1. Mise à jour visuelle optimiste des lignes de la page courante
					const table = cell.getTable();
					const rows = table.getRows();
					rows.forEach(function (row: import('tabulator-tables').RowComponent) {
						const d = row.getData();
						if (enabledWhenField && !d[enabledWhenField]) return;
						d[field] = checked;
						row.reformat(); // Force le rafraîchissement visuel immédiat
					});

					// 2. Notifier le contrôleur via le sentinel _selectAll
					if (onChangeActionKey) {
						const handler = actionRegistry[onChangeActionKey];
						if (handler) {
							applyScope($scope, () => handler({ _selectAll: true }, checked));
						}
					}
				});

				container.appendChild(chk);
				return container;
			},
			field: col.field,
			width: w,
			minWidth: w,
			maxWidth: w,
			hozAlign: 'center',
			headerSort: false,
			formatter: MassiaTabulatorHelpers.makeCheckboxColumn($scope, {
				field: col.field,
				enabledWhen: enabledWhenField ? (d: Record<string, unknown>) => !!d[enabledWhenField] : undefined,
				onChange: function (d: Record<string, unknown>, checked: boolean) {
					d[col.field] = checked;
					if (col.onChangeActionKey) {
						const handler = actionRegistry[col.onChangeActionKey];
						if (handler) handler(d, checked);
					}
				}
			}),
			resizable: false,
			visible: col.visible
		};
		return colDef;
	}

	private buildIcon(col: IconServerColumnDef, titleRegistry: TitleRegistry): MassiaTabulatorColumnDef {
		const enabledWhenField = col.enabledWhenField;
		const iconClass = this.resolveIconClass(col.iconClass || '');

		const w = col.width || 55;

		return {
			title: this.resolveTitle(col) || iconClass || col.field, // Titre texte pour le menu global
			titleFormatter: () => `<span class="${iconClass}"></span>`, // Rendu visuel de l'icône dans le header
			field: col.field,
			width: w,
			minWidth: w,
			maxWidth: w,
			hozAlign: 'center',
			headerSort: col.sortable !== false,
			resizable: false,
			formatter: MassiaTabulatorHelpers.makeIconColumn({
				icon: iconClass,
				enabledWhen: enabledWhenField ? (d: Record<string, unknown>) => !!d[enabledWhenField] : undefined,
				getTitle: (d: Record<string, unknown>) => {
					let title = col.translateKey ? this.$translate.instant(col.translateKey) : '';
					if (col.getTitleActionKey && titleRegistry[col.getTitleActionKey]) {
						title = titleRegistry[col.getTitleActionKey](d);
					}
					return title;
				}
			})
		};
	}

	private buildActions(
		col: ActionServerColumnDef,
		actionRegistry: ActionRegistry,
		permissions: Record<string, boolean>,
		$scope?: ng.IScope
	): MassiaTabulatorColumnDef {
		const actionDefs = (col.actions || [])
			.filter((action) => {
				if (action.requiredPermission) {
					return !!permissions[action.requiredPermission];
				}
				return true;
			})
			.map((action) => {
				const finalIcon = this.resolveIconClass(action.icon || '');
				return {
					icon: finalIcon,
					title: action.translateKey ? this.$translate.instant(action.translateKey) : action.key,
					visible: this.buildVisibilityFn(action),
					disabled: action.disabledWhenField ? (d: Record<string, unknown>) => !!d[action.disabledWhenField!] : false,
					onClick: function (d: unknown) {
						const handler = actionRegistry[action.key];
						if (handler) {
							handler(d);
						} else {
							console.warn(`[GridConfigAdapter] No handler for action "${action.key}"`);
						}
					}
				};
			});

		return {
			title: this.resolveTitle(col) || this.$translate.instant('ACTIONS'),
			field: col.field || 'id',
			width: col.width || 180,
			minWidth: col.width || 180,
			maxWidth: col.width || 180,
			hozAlign: 'center',
			headerSort: false,
			resizable: false,
			formatter: MassiaTabulatorHelpers.makeActionColumn($scope, actionDefs)
		};
	}

	private buildCustom(col: CustomServerColumnDef, actionRegistry: ActionRegistry): MassiaTabulatorColumnDef {
		const formatterKey = col.formatterKey || 'formatter_' + col.field;
		const { headerFilter, headerFilterParams } = this.resolveHeaderFilter(col);
		return {
			...this.buildColumnBase(col),
			hozAlign: col.hozAlign || 'left',
			headerFilter,
			headerFilterParams: headerFilterParams as ColumnDefinition['headerFilterParams'],
			formatter: function (cell: import('tabulator-tables').CellComponent, params?: unknown, onRendered?: unknown) {
				const fn = actionRegistry[formatterKey];
				return fn ? fn(cell, params, onRendered) : cell.getValue() || '';
			}
		} as MassiaTabulatorColumnDef;
	}

	private buildHrefFormatter(hrefDef: HrefDefinition, $state: StateService): (cell: import('tabulator-tables').CellComponent) => string {
		return function (cell: import('tabulator-tables').CellComponent) {
			const value = cell.getValue();
			const params: Record<string, unknown> = {};
			for (const key in hrefDef.hrefDataField) {
				params[key] = cell.getData()[hrefDef.hrefDataField[key]] || hrefDef.hrefDataField[key];
			}
			const href = $state.href(hrefDef.route, params);
			if (href) {
				return value
					? '<span class="glyphicon glyphicon-share"></span> <a target="_blank" href="' +
							href +
							'" style="text-decoration:underline;">' +
							value +
							'</a>'
					: '';
			}
			return value;
		};
	}

	// ─── Helpers ──────────────────────────────────────────────────────────

	private resolveTitle(col: ServerColumnDef): string {
		if (col.translateKey) return this.$translate.instant(col.translateKey);
		return col.title || '';
	}

	private resolveIconClass(icon: string): string {
		if (icon && !icon.includes(' ')) return `glyphicon glyphicon-${icon}`;
		return icon;
	}

	private resolveHeaderFilter(col: { filterable?: boolean; headerFilterType?: string; headerFilterParams?: unknown }): {
		headerFilter: ColumnDefinition['headerFilter'];
		headerFilterParams: unknown;
	} {
		if (!col.filterable) return { headerFilter: undefined, headerFilterParams: undefined };
		return {
			headerFilter: (col.headerFilterType || 'input') as ColumnDefinition['headerFilter'],
			headerFilterParams: col.headerFilterParams
		};
	}

	private buildTitleContainer(headerLabel: string, tooltip?: string): HTMLDivElement {
		const container = document.createElement('div');
		container.style.cssText = 'display:flex; flex-direction:column; align-items:center; gap:2px;';
		if (headerLabel) {
			const label = document.createElement('span');
			label.title = tooltip || headerLabel;
			label.textContent = headerLabel;
			label.style.cssText = 'white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:100%;';
			container.appendChild(label);
		}
		return container;
	}

	private buildTitleFormatter(headerLabel: string, tooltip?: string) {
		return () => this.buildTitleContainer(headerLabel, tooltip);
	}

	private buildColumnBase(col: ServerColumnDef): Partial<MassiaTabulatorColumnDef> {
		const headerLabel = this.resolveTitle(col);
		return {
			titleFormatter: this.buildTitleFormatter(headerLabel),
			title: headerLabel,
			field: col.field,
			minWidth: col.minWidth,
			width: col.width,
			headerSort: col.sortable !== false,
			resizable: col.resizable !== false
		};
	}

	private buildVisibilityFn(action: ServerActionDef): (d: Record<string, unknown>) => boolean {
		if (action.visibleWhenAllFields?.length) {
			return function (d: Record<string, unknown>) {
				const ok = action.visibleWhenAllFields!.every(function (rule) {
					const negate = rule.startsWith('!');
					const field = negate ? rule.slice(1) : rule;
					return negate ? !d[field] : !!d[field];
				});
				return ok;
			};
		}
		if (action.visibleWhenFieldNot) {
			const f = action.visibleWhenFieldNot;
			return (d: Record<string, unknown>) => !d[f];
		}
		if (action.visibleWhenField) {
			const f = action.visibleWhenField;
			return (d: Record<string, unknown>) => !!d[f];
		}
		return () => true;
	}
}

GridConfigAdapterService.$inject = ['$translate', 'globalizationManagementService', 'moment'];
